import { createContext, useEffect, useReducer, useContext } from "react";
import jwtDecode from "jwt-decode";
// utils
import axios from "utils/axios";
import { toast } from "react-toastify";
import { TOKEN_KEY } from "constants";

// JWT utils
const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (accessToken, user) => {
  if (accessToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    // This function below will handle when token is expired
    // const { exp } = jwtDecode(accessToken);
    // handleTokenExpired(exp);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("user");
    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isInitialized: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isInitialized: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem(TOKEN_KEY);
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken, null);

          const response = await axios.get("/api/users/me");
          const user = response.data;

          setSession(accessToken, user);

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    logout();
    try {
      const response = await axios.post("/api/auth/local", {
        identifier: email,
        password,
      });
      const { jwt } = response.data;
      setSession(jwt, null);

      const userRes = await axios.get("/api/users/me");
      const user = userRes.data;

      setSession(jwt, user);

      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });

      return user;
    } catch (error) {
      console.error(error.error.message);
      toast.error(error.error.message);
      return null;
    }
  };

  const register = async (firstName, lastName, email, password) => {
    logout();
    const response = await axios.post("/api/auth/local/register", {
      firstName,
      lastName,
      email,
      password,
    });
    const { jwt } = response.data;
    setSession(jwt, null);

    const userRes = await axios.get("/api/users/me");
    const user = userRes.data;

    setSession(jwt, user);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });

    return user;
  };

  const logout = () => {
    console.log("Loggin Out");
    setSession(null, null);
    dispatch({ type: "LOGOUT" });
  };

  const refreshUser = async () => {
    try {
      const accessToken = window.localStorage.getItem(TOKEN_KEY);
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, null);

        const response = await axios.get("/api/users/me");
        const user = response.data;

        setSession(accessToken, user);

        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: "INITIALIZE",
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error("Auth context must be use inside AuthProvider");

  return context;
};

export default useAuth;

export { AuthContext, AuthProvider };
